import React, { useState, useMemo, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { userService } from '../api/userService';
import { Users as UsersIcon, ChevronDown, ChevronRight, FolderOpen, FolderClosed } from 'lucide-react';
import { DIVISIONS, ORGANOGRAM_HIERARCHY, STRATEGIC_HIERARCHY } from '../constants/divisions';

const EMPTY_ARRAY = [];

const Organogram = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('pillars'); // 'pillars' | 'strategic' | 'classic'
    const [hoveredAreaId, setHoveredAreaId] = useState(null);
    const [clickSelectedAreaId, setClickSelectedAreaId] = useState(null);
    
    // Manage expanded state for each pillar/strategic group
    const [expandedGroups, setExpandedGroups] = useState({});

    // References for dynamic SVG connector lines
    const containerRef = useRef(null);
    const dgRef = useRef(null);
    const cardRefs = useRef([]);
    const [lines, setLines] = useState([]);
    const [hoveredIdx, setHoveredIdx] = useState(null);

    const { data: usersData = EMPTY_ARRAY } = useQuery({
        queryKey: ['dg_users'],
        queryFn: () => userService.getAllUsers().catch(() => []),
        staleTime: 300000
    });


    // 1. Resolve matching users from Cognito groups to the structured organogram areas (for Pillars & Strategic views)
    const areaMembers = useMemo(() => {
        const members = {};
        
        const allHierarchies = [...ORGANOGRAM_HIERARCHY, ...STRATEGIC_HIERARCHY];
        allHierarchies.forEach(pillar => {
            pillar.areas.forEach(area => {
                members[area.id] = { coordinators: [], employees: [] };
            });
        });

        const allUsers = Array.isArray(usersData) ? usersData : (usersData?.Users || []);

        allUsers.forEach(user => {
            const rawRoles = user.Groups || user.groups || [];
            const roles = (Array.isArray(rawRoles) ? rawRoles : [rawRoles]).map(r => r.toUpperCase());
            const divAttr = (user.Division || user.division || "").toUpperCase();
            const userName = user.Name || user.Username || "";
            const lowerName = userName.toLowerCase();

            const userDivisions = [];
            DIVISIONS.forEach(div => {
                const isCoord = roles.includes(`COORDINATOR_${div.id}`);
                const isEmp = roles.includes(`EMPLOYEE_${div.id}`);
                const isImplicit = divAttr === div.id || divAttr === div.name.toUpperCase();
                if (isCoord || isEmp || isImplicit) {
                    userDivisions.push({ divId: div.id, isCoord });
                }
            });

            userDivisions.forEach(({ divId, isCoord }) => {
                const matchingAreas = [];
                allHierarchies.forEach(pillar => {
                    pillar.areas.forEach(area => {
                        const areaDivId = area.parentDivId || area.id;
                        if (areaDivId === divId) {
                            if (!matchingAreas.some(a => a.id === area.id)) {
                                matchingAreas.push(area);
                            }
                        }
                    });
                });

                if (matchingAreas.length === 0) return;

                if (matchingAreas.length === 1) {
                    const targetAreaId = matchingAreas[0].id;
                    if (isCoord) {
                        if (!members[targetAreaId].coordinators.includes(userName)) {
                            members[targetAreaId].coordinators.push(userName);
                        }
                    } else {
                        if (!members[targetAreaId].coordinators.includes(userName) && !members[targetAreaId].employees.includes(userName)) {
                            members[targetAreaId].employees.push(userName);
                        }
                    }
                    return;
                }

                let assignedAreaId = null;
                for (const area of matchingAreas) {
                    const leadStr = (area.lead || '').toLowerCase();
                    const cleanLead = leadStr.replace(/dr\.|mr\.|ms\./g, '').trim();
                    const cleanName = lowerName.replace(/dr\.|mr\.|ms\./g, '').trim();

                    if (cleanLead && (cleanName.includes(cleanLead) || cleanLead.includes(cleanName))) {
                        assignedAreaId = area.id;
                        break;
                    }
                }

                if (!assignedAreaId) {
                    const primaryArea = matchingAreas.find(a => !a.parentDivId) || matchingAreas[0];
                    assignedAreaId = primaryArea.id;
                }

                if (isCoord) {
                    if (!members[assignedAreaId].coordinators.includes(userName)) {
                        members[assignedAreaId].coordinators.push(userName);
                    }
                } else {
                    if (!members[assignedAreaId].coordinators.includes(userName) && !members[assignedAreaId].employees.includes(userName)) {
                        members[assignedAreaId].employees.push(userName);
                    }
                }
            });
        });
        return members;
    }, [usersData]);

    // 2. Map users directly to standard divisions (for the Classic Flat view)
    const divisionMembers = useMemo(() => {
        const members = {};
        DIVISIONS.forEach(div => {
            members[div.name] = { coordinators: [], employees: [] };
        });

        const allUsers = Array.isArray(usersData) ? usersData : (usersData?.Users || []);

        allUsers.forEach(user => {
            const rawRoles = user.Groups || user.groups || [];
            const roles = Array.isArray(rawRoles) ? rawRoles : [rawRoles];
            const divAttr = (user.Division || user.division || "").toUpperCase();
            
            DIVISIONS.forEach(div => {
                const isCoord = roles.some(r => r.toUpperCase() === `COORDINATOR_${div.id}`);
                const isEmp = roles.some(r => r.toUpperCase() === `EMPLOYEE_${div.id}`);
                const isImplicit = divAttr === div.id || divAttr === div.name.toUpperCase();
                const userName = user.Name || user.Username;

                if (isCoord) {
                    if (!members[div.name].coordinators.includes(userName)) {
                        members[div.name].coordinators.push(userName);
                    }
                } else if (isEmp || isImplicit) {
                    if (!members[div.name].coordinators.includes(userName) && !members[div.name].employees.includes(userName)) {
                        members[div.name].employees.push(userName);
                    }
                }
            });
        });
        return members;
    }, [usersData]);

    // Determine target groups based on active tab
    const groups = useMemo(() => {
        if (activeTab === 'pillars') return ORGANOGRAM_HIERARCHY;
        if (activeTab === 'strategic') return STRATEGIC_HIERARCHY;
        return DIVISIONS;
    }, [activeTab]);

    // Resize/cleanup refs
    cardRefs.current = cardRefs.current.slice(0, groups.length);

    const recalcLines = () => {
        if (!containerRef.current || !dgRef.current) return;
        const containerRect = containerRef.current.getBoundingClientRect();
        const dgRect = dgRef.current.getBoundingClientRect();

        const dgCenterX = dgRect.left - containerRect.left + dgRect.width / 2;
        const dgBottomY = dgRect.bottom - containerRect.top;

        const newLines = cardRefs.current.map((ref, idx) => {
            if (!ref) return null;
            const rect = ref.getBoundingClientRect();
            const cardCenterX = rect.left - containerRect.left + rect.width / 2;
            const cardTopY = rect.top - containerRect.top;
            return { idx, x1: dgCenterX, y1: dgBottomY, x2: cardCenterX, y2: cardTopY };
        }).filter(Boolean);

        setLines(prevLines => {
            if (prevLines.length !== newLines.length) return newLines;
            const hasChanged = newLines.some((line, i) => {
                const prev = prevLines[i];
                return !prev ||
                       prev.x1 !== line.x1 ||
                       prev.y1 !== line.y1 ||
                       prev.x2 !== line.x2 ||
                       prev.y2 !== line.y2;
            });
            return hasChanged ? newLines : prevLines;
        });
    };

    useLayoutEffect(() => {
        recalcLines();

        if (!containerRef.current) return;

        // Use ResizeObserver to track any element-level width/height shifts (e.g. sidebar transitions)
        const observer = new ResizeObserver(() => {
            recalcLines();
        });
        observer.observe(containerRef.current);

        window.addEventListener('resize', recalcLines);

        // Fallback timer for delayed layout shifts
        const timer = setTimeout(recalcLines, 150);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', recalcLines);
            clearTimeout(timer);
        };
    }, [activeTab, expandedGroups, usersData, groups]);

    const handleAreaClick = (areaId, parentDivId) => {
        const targetDivId = parentDivId || areaId;
        navigate(`/division/${targetDivId}`);
    };

    const toggleGroup = (groupId) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupId]: !prev[groupId]
        }));
    };

    const expandAll = () => {
        const allGroups = {};
        ORGANOGRAM_HIERARCHY.forEach(p => { allGroups[p.id] = true; });
        STRATEGIC_HIERARCHY.forEach(p => { allGroups[p.id] = true; });
        DIVISIONS.forEach(d => { allGroups[d.id] = true; });
        setExpandedGroups(allGroups);
    };

    const collapseAll = () => {
        setExpandedGroups({});
    };

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        setHoveredAreaId(null);
        setClickSelectedAreaId(null);
        setHoveredIdx(null);
    };

    return (
        <div
            ref={containerRef}
            style={{
                width: '100%',
                padding: '2rem 1.5rem',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                backgroundColor: '#f8fafc',
                minHeight: '100vh',
                position: 'relative',
                overflowX: 'hidden'
            }}
        >
            {/* SVG connector overlay */}
            <svg
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    zIndex: 0,
                    overflow: 'visible',
                }}
                aria-hidden="true"
            >
                {lines.map((l) => (
                    <path
                        key={l.idx}
                        d={`M ${l.x1} ${l.y1} C ${l.x1} ${(l.y1 + l.y2) / 2}, ${l.x2} ${(l.y1 + l.y2) / 2}, ${l.x2} ${l.y2}`}
                        stroke={hoveredIdx === l.idx ? groups[l.idx]?.color || '#cbd5e1' : '#cbd5e1'}
                        strokeWidth={hoveredIdx === l.idx ? 2.5 : 1.5}
                        fill="none"
                        style={{
                            transition: 'stroke 0.2s, stroke-width 0.2s',
                            opacity: hoveredIdx === null || hoveredIdx === l.idx ? 1 : 0.45
                        }}
                    />
                ))}
            </svg>

            {/* Tab Selector and Global Expand/Collapse buttons */}
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '1rem',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: '2.5rem',
                zIndex: 2
            }}>
                <div style={{
                    display: 'inline-flex',
                    background: '#f1f5f9',
                    border: '1px solid #e2e8f0',
                    padding: '4px',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                }}>
                    {[
                        { id: 'pillars', label: 'Functional Pillars' },
                        { id: 'strategic', label: 'Strategic Units' },
                        { id: 'classic', label: 'All Divisions (Flat List)' }
                    ].map(tab => {
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => handleTabChange(tab.id)}
                                style={{
                                    padding: '0.5rem 1.25rem',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '0.78rem',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    background: isActive ? '#ffffff' : 'transparent',
                                    color: isActive ? '#0f172a' : '#64748b',
                                    boxShadow: isActive ? '0 2px 6px rgba(0,0,0,0.06)' : 'none',
                                    transition: 'all 0.15s ease',
                                    outline: 'none'
                                }}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {activeTab !== 'classic' && (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                            onClick={expandAll}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '0.5rem 1rem',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                background: '#ffffff',
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                color: '#475569',
                                cursor: 'pointer',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                            }}
                        >
                            <FolderOpen size={13} />
                            Expand All
                        </button>
                        <button
                            onClick={collapseAll}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '0.5rem 1rem',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                background: '#ffffff',
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                color: '#475569',
                                cursor: 'pointer',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                            }}
                        >
                            <FolderClosed size={13} />
                            Collapse All
                        </button>
                    </div>
                )}
            </div>

            {/* Tree Flow Container */}
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                
                {/* RIS Central Node */}
                <div
                    ref={dgRef}
                    style={{
                        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                        color: '#fff',
                        borderRadius: '16px',
                        padding: '1rem 3rem',
                        boxShadow: '0 8px 25px rgba(15, 23, 42, 0.12)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        cursor: 'default',
                        userSelect: 'none',
                        minWidth: '220px',
                        zIndex: 2,
                        marginBottom: '3.5rem',
                        textAlign: 'center'
                    }}
                >
                    <div style={{ fontWeight: 800, fontSize: '1.05rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        Research & Information System
                    </div>
                </div>

                {/* Horizontal Level-1 Flex-wrap Container */}
                <div style={{
                    zIndex: 3,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '3rem 1.5rem',
                    justifyContent: 'center',
                    width: '100%',
                    position: 'relative',
                    boxSizing: 'border-box',
                    padding: '0 1rem'
                }}>
                    {groups.map((group, idx) => {
                        const isHovered = hoveredIdx === idx;
                        const isExpanded = !!expandedGroups[group.id];

                        // Classic Tab handles flat divisions
                        if (activeTab === 'classic') {
                            const isDivHovered = hoveredAreaId === group.id;
                            const totalMembers = (divisionMembers[group.name]?.coordinators.length || 0) + (divisionMembers[group.name]?.employees.length || 0);
                            return (
                                <div
                                    key={group.id}
                                    ref={(el) => { cardRefs.current[idx] = el; }}
                                    onMouseEnter={() => {
                                        setHoveredIdx(idx);
                                        setHoveredAreaId(group.id);
                                    }}
                                    onMouseLeave={() => {
                                        setHoveredIdx(null);
                                        setHoveredAreaId(null);
                                    }}
                                    onClick={(e) => {
                                        setClickSelectedAreaId(clickSelectedAreaId === group.id ? null : group.id);
                                        e.stopPropagation();
                                    }}
                                    onDoubleClick={() => handleAreaClick(group.id)}
                                    style={{
                                        width: '220px',
                                        height: '64px',
                                        boxSizing: 'border-box',
                                        background: isDivHovered ? group.color : '#ffffff',
                                        border: `2px solid ${group.color}`,
                                        borderRadius: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '0.6rem 0.9rem',
                                        cursor: 'pointer',
                                        boxShadow: isDivHovered
                                            ? `0 8px 20px ${group.color}35`
                                            : '0 2px 8px rgba(0,0,0,0.03)',
                                        transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
                                        transform: isDivHovered ? 'translateY(-4px) scale(1.03)' : 'none',
                                        userSelect: 'none',
                                        position: 'relative',
                                        zIndex: isDivHovered || clickSelectedAreaId === group.id ? 10 : 2
                                    }}
                                >
                                    <div style={{ textAlign: 'left', maxWidth: '140px' }}>
                                        <div style={{
                                            fontSize: '0.78rem',
                                            fontWeight: 800,
                                            color: isDivHovered ? '#ffffff' : group.color,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            {group.name}
                                        </div>
                                        <div style={{
                                            fontSize: '0.62rem',
                                            fontWeight: 650,
                                            color: isDivHovered ? 'rgba(255,255,255,0.8)' : '#64748b',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            marginTop: '1px'
                                        }}>
                                            {group.fullName}
                                        </div>
                                    </div>

                                    <div style={{
                                        background: isDivHovered ? 'rgba(255,255,255,0.2)' : `${group.color}15`,
                                        padding: '2px 8px',
                                        borderRadius: '99px',
                                        fontSize: '0.62rem',
                                        fontWeight: 700,
                                        color: isDivHovered ? '#ffffff' : group.color,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '3px'
                                    }}>
                                        <UsersIcon size={8} />
                                        {totalMembers}
                                    </div>

                                    {/* Classic Popover */}
                                    {(isDivHovered || clickSelectedAreaId === group.id) && (
                                        <div style={{
                                            position: 'absolute',
                                            bottom: 'calc(100% + 10px)',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            background: 'rgba(15, 23, 42, 0.96)',
                                            color: '#f8fafc',
                                            fontSize: '0.72rem',
                                            fontWeight: 500,
                                            padding: '0.85rem',
                                            borderRadius: '14px',
                                            whiteSpace: 'normal',
                                            zIndex: 100,
                                            lineHeight: 1.4,
                                            minWidth: '220px',
                                            maxWidth: '280px',
                                            boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
                                            backdropFilter: 'blur(8px)',
                                            border: '1px solid rgba(255,255,255,0.08)',
                                            cursor: 'default'
                                        }} onClick={(e) => e.stopPropagation()}>
                                            <div style={{ fontWeight: 850, marginBottom: '0.5rem', color: group.color, fontSize: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '0.4rem' }}>{group.fullName}</div>
                                            {divisionMembers[group.name]?.coordinators.length > 0 && (
                                                <div style={{ marginBottom: '0.5rem' }}>
                                                    <div style={{ fontSize: '0.58rem', textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.05em', marginBottom: '0.15rem', fontWeight: 700 }}>Coordinators</div>
                                                    {divisionMembers[group.name].coordinators.map(name => <div key={name}>{name}</div>)}
                                                </div>
                                            )}
                                            {divisionMembers[group.name]?.employees.length > 0 && (
                                                <div style={{ marginBottom: '0.5rem' }}>
                                                    <div style={{ fontSize: '0.58rem', textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.05em', marginBottom: '0.15rem', fontWeight: 700 }}>Staff</div>
                                                    {divisionMembers[group.name].employees.map(name => <div key={name}>{name}</div>)}
                                                </div>
                                            )}
                                            {totalMembers === 0 && <div style={{ fontSize: '0.62rem', color: '#64748b', fontStyle: 'italic' }}>No members found.</div>}
                                            <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '0.4rem', color: '#38bdf8', fontSize: '0.6rem', fontWeight: 600 }}>Double-click to view dashboard</div>
                                        </div>
                                    )}
                                </div>
                            );
                        }

                        // Pillars & Strategic tabs render hierarchical column branches
                        return (
                            <div
                                key={group.id}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    width: '220px',
                                    position: 'relative',
                                    zIndex: isHovered || group.areas?.some(a => hoveredAreaId === a.id || clickSelectedAreaId === a.id) ? 10 : 2
                                }}
                            >
                                {/* Level-1 Pillar / Strategic Unit Card */}
                                <div
                                    ref={(el) => { cardRefs.current[idx] = el; }}
                                    onMouseEnter={() => {
                                        setHoveredIdx(idx);
                                    }}
                                    onMouseLeave={() => {
                                        setHoveredIdx(null);
                                    }}
                                    onClick={() => toggleGroup(group.id)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        background: '#ffffff',
                                        border: `1px solid #e2e8f0`,
                                        borderLeft: `5px solid ${group.color}`,
                                        borderRadius: '12px',
                                        padding: '0.75rem 0.95rem',
                                        boxShadow: isHovered
                                            ? `0 8px 20px rgba(0,0,0,0.05)`
                                            : '0 2px 6px rgba(0,0,0,0.02)',
                                        width: '100%',
                                        boxSizing: 'border-box',
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        transition: 'all 0.2s ease',
                                        transform: isHovered ? 'translateY(-3px)' : 'none',
                                        zIndex: 2
                                    }}
                                >
                                    <div style={{ textAlign: 'left', maxWidth: '160px' }}>
                                        <div style={{
                                            fontSize: '0.55rem',
                                            fontWeight: 800,
                                            color: group.color,
                                            background: `${group.color}12`,
                                            padding: '1px 6px',
                                            borderRadius: '99px',
                                            display: 'inline-block',
                                            marginBottom: '3px',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            {group.strategicUnit}
                                        </div>
                                        <h4 style={{
                                            margin: 0,
                                            fontSize: '0.8rem',
                                            fontWeight: 850,
                                            color: '#0f172a',
                                            lineHeight: 1.25,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            {group.name}
                                        </h4>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', marginLeft: '4px' }}>
                                        {isExpanded ? <ChevronDown size={14} color="#64748b" /> : <ChevronRight size={14} color="#64748b" />}
                                    </div>
                                </div>

                                {/* Level-2 Areas vertical stack list */}
                                {isExpanded && group.areas && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            width: '100%',
                                            marginTop: '0.6rem',
                                            position: 'relative'
                                        }}
                                    >
                                        {/* Straight vertical connector segment from Level-1 Card to Level-2 List */}
                                        <div
                                            style={{
                                                width: '2px',
                                                height: '14px',
                                                background: group.color,
                                                marginBottom: '0.4rem',
                                                zIndex: 0
                                            }}
                                        />

                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '0.75rem',
                                                width: '100%',
                                                position: 'relative'
                                            }}
                                        >
                                            {/* Vertical line running down the center behind the cards */}
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    bottom: '24px', // Ends at the center-bottom of the last card
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    width: '2px',
                                                    background: group.color,
                                                    zIndex: 0
                                                }}
                                            />

                                            {group.areas.map(area => {
                                                const isAreaHovered = hoveredAreaId === area.id;
                                                const totalMembers = (areaMembers[area.id]?.coordinators.length || 0) + (areaMembers[area.id]?.employees.length || 0);

                                                return (
                                                    <div
                                                        key={area.id}
                                                        onMouseEnter={() => setHoveredAreaId(area.id)}
                                                        onMouseLeave={() => setHoveredAreaId(null)}
                                                        onClick={(e) => {
                                                            setClickSelectedAreaId(clickSelectedAreaId === area.id ? null : area.id);
                                                            e.stopPropagation();
                                                        }}
                                                        onDoubleClick={() => handleAreaClick(area.id, area.parentDivId)}
                                                        style={{
                                                            background: '#ffffff',
                                                            border: isAreaHovered ? `1px solid ${group.color}` : '1px solid #e2e8f0',
                                                            borderRadius: '10px',
                                                            padding: '0.5rem 0.8rem',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            width: '100%',
                                                            boxSizing: 'border-box',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.15s ease',
                                                            transform: isAreaHovered ? 'translateY(-2px)' : 'none',
                                                            position: 'relative',
                                                            zIndex: isAreaHovered ? 11 : 1,
                                                            boxShadow: isAreaHovered ? '0 4px 10px rgba(0,0,0,0.04)' : '0 1px 3px rgba(0,0,0,0.01)'
                                                        }}
                                                    >
                                                        <div style={{ textAlign: 'left', maxWidth: '140px' }}>
                                                            <div style={{
                                                                fontSize: '0.74rem',
                                                                fontWeight: 700,
                                                                color: '#475569',
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis'
                                                            }}>
                                                                {area.name}
                                                            </div>
                                                            <div style={{
                                                                fontSize: '0.6rem',
                                                                color: '#94a3b8',
                                                                marginTop: '2px',
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis'
                                                            }}>
                                                                {area.lead}
                                                            </div>
                                                        </div>

                                                        <div style={{
                                                            background: isAreaHovered ? `${group.color}15` : '#f1f5f9',
                                                            padding: '1px 6px',
                                                            borderRadius: '99px',
                                                            fontSize: '0.58rem',
                                                            fontWeight: 700,
                                                            color: isAreaHovered ? group.color : '#64748b',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '2px',
                                                            marginLeft: '4px'
                                                        }}>
                                                            <UsersIcon size={8} />
                                                            {totalMembers}
                                                        </div>

                                                        {/* Area Popover */}
                                                        {(isAreaHovered || clickSelectedAreaId === area.id) && (
                                                            <div style={{
                                                                position: 'absolute',
                                                                bottom: 'calc(100% + 10px)',
                                                                left: '50%',
                                                                transform: 'translateX(-50%)',
                                                                background: 'rgba(15, 23, 42, 0.96)',
                                                                color: '#f8fafc',
                                                                fontSize: '0.72rem',
                                                                fontWeight: 500,
                                                                padding: '0.85rem',
                                                                borderRadius: '14px',
                                                                zIndex: 100,
                                                                minWidth: '220px',
                                                                maxWidth: '280px',
                                                                boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
                                                                border: '1px solid rgba(255,255,255,0.08)',
                                                                cursor: 'default'
                                                            }} onClick={(e) => e.stopPropagation()}>
                                                                <div style={{ fontWeight: 800, color: group.color, borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '0.25rem', marginBottom: '0.4rem' }}>{area.name}</div>
                                                                <div style={{ marginBottom: '0.4rem' }}>
                                                                    <div style={{ fontSize: '0.58rem', color: '#94a3b8' }}>Lead</div>
                                                                    <div style={{ fontWeight: 600 }}>{area.lead}</div>
                                                                </div>
                                                                {areaMembers[area.id]?.coordinators.length > 0 && (
                                                                    <div style={{ marginBottom: '0.4rem' }}>
                                                                        <div style={{ fontSize: '0.58rem', color: '#94a3b8' }}>Coordinators</div>
                                                                        {areaMembers[area.id].coordinators.map(n => <div key={n}>{n}</div>)}
                                                                    </div>
                                                                )}
                                                                {areaMembers[area.id]?.employees.length > 0 && (
                                                                    <div>
                                                                        <div style={{ fontSize: '0.58rem', color: '#94a3b8' }}>Staff</div>
                                                                        {areaMembers[area.id].employees.map(n => <div key={n}>{n}</div>)}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default Organogram;
